import { useEffect } from 'react';
import { createGameLog, expandGameLog, recordPlaceMove, recordSwapMove } from '../utils/gameRecorder';
import { subscribeToRoom } from '../utils/supabaseActions';

export const useGameOrchestratorRealtime = ({
  isBotFight,
  roomCode,
  screen,
  gameEnded,
  isSpectator,
  setIsConnectionOffline,
  setIsConnectionRecovering,
  setRoomData,
  setPlayers,
  setGameBoard,
  setGameEnded,
  setFinalGamePlayers,
  setShowGameEndModal,
  setEndGamePlayers,
  setCurrentPlayer,
  setMarkersPool,
  setMoveHistory,
  setScreen,
  setLastExecutedAITurn,
  playersRef,
  addToast,
  t,
  gameLogRef,
  gameStartTimeRef,
  gameEndingRef,
}) => {
  useEffect(() => {
    if (isBotFight) {
      return undefined;
    }

    if (!roomCode || (screen !== 'room' && screen !== 'game')) {
      return undefined;
    }

    const unsubscribe = subscribeToRoom(roomCode, (updatedRoom) => {
      setIsConnectionOffline(false);
      setIsConnectionRecovering(false);
      if (!updatedRoom.status) {
        return;
      }

      setRoomData(updatedRoom);

      if (updatedRoom.status === 'finished' && updatedRoom.game_state && !gameEndingRef.current) {
        const gs = updatedRoom.game_state;
        const finalPlayers = gs.players || [];

        if (isSpectator) {
          setPlayers(finalPlayers);
          setGameBoard(gs.gameBoard);
          setGameEnded(true);
          const sorted = [...finalPlayers].sort((a, b) => b.score - a.score);
          setFinalGamePlayers(sorted);
          setShowGameEndModal(true);
          return;
        }

        setEndGamePlayers(finalPlayers);
        return;
      }

      if (updatedRoom.status !== 'playing' || !updatedRoom.game_state) {
        return;
      }

      if (!gameStartTimeRef.current) {
        gameStartTimeRef.current = Date.now();
      }

      const gs = updatedRoom.game_state;
      const gamePlayers = gs.players || [];

      let validCurrentPlayer = gs.currentPlayer || 0;
      if (validCurrentPlayer >= gamePlayers.length) {
        validCurrentPlayer = Math.max(0, gamePlayers.length - 1);
      }

      setGameBoard(gs.gameBoard);

      if (screen === 'game' && !isSpectator) {
        gamePlayers.forEach((player, index) => {
          const previousPlayer = playersRef.current[index];
          if (previousPlayer && previousPlayer.isAI && !player.isAI) {
            addToast(`${player.name} ${t('playerRejoined')}`, 'info', 5000);
          }
          if (previousPlayer && !previousPlayer.isAI && player.isAI) {
            addToast(`${player.name} ${t('playerReplacedByAI')}`, 'info', 5000);
          }
        });
      }

      setPlayers(gamePlayers);
      setCurrentPlayer(validCurrentPlayer);
      setMarkersPool(gs.markersPool);
      if (gs.moves) {
        setMoveHistory(gs.moves);
      }

      if (!isSpectator) {
        if (screen === 'room') {
          if (!gameLogRef.current) {
            if (gs.game_log_initial) {
              gameLogRef.current = expandGameLog(gs.game_log_initial);
            } else {
              gameLogRef.current = createGameLog(gs.gameBoard, gamePlayers, gs.markersPool || [], validCurrentPlayer);
            }
            try {
              localStorage.setItem(`gamelog_${roomCode}`, JSON.stringify(gameLogRef.current));
            } catch {
              // Ignore storage failures.
            }
          }
        } else if (screen === 'game' && gs.moves && gs.gameBoard) {
          if (!gameLogRef.current && gs.game_log_initial) {
            gameLogRef.current = expandGameLog(gs.game_log_initial);
          }

          if (gameLogRef.current) {
            const loggedCount = gameLogRef.current.moves.length;
            const serverMoves = gs.moves;
            if (serverMoves.length > loggedCount) {
              for (let i = loggedCount; i < serverMoves.length; i++) {
                const move = serverMoves[i];
                if (move.type === 'place') {
                  const colorIndex = typeof gs.gameBoard[move.row]?.[move.col] === 'number'
                    ? gs.gameBoard[move.row][move.col]
                    : null;
                  if (colorIndex !== null) {
                    recordPlaceMove(gameLogRef.current, move.player, colorIndex, move.row, move.col);
                  }
                } else if (move.type === 'swap') {
                  recordSwapMove(gameLogRef.current, move.player, move.target);
                }
              }
              try {
                localStorage.setItem(`gamelog_${roomCode}`, JSON.stringify(gameLogRef.current));
              } catch {
                // Ignore storage failures.
              }
            }
          }
        }
      }

      if (screen === 'room') {
        setScreen('game');
        setGameEnded(false);
        setLastExecutedAITurn(null);
      }
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, screen, gameEnded, isSpectator]);
};
